import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { Capacitor } from '@capacitor/core';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Buildings from './pages/Buildings';
import Flats from './pages/Flats';
import Payments from './pages/Payments';
import Complaints from './pages/Complaints';
import MaintenanceStaff from './pages/MaintenanceStaff';
import Notifications from './pages/Notifications';
import Login from './pages/Login';
import LoadingSpinner from './components/LoadingSpinner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import OwnerTenantDashboard from './pages/OwnerTenantDashboard';
import Profile from './pages/Profile';
import VisitorDashboard from './pages/VisitorDashboard'; // <-- Add this import
import BuildingAdminDashboard from './pages/BuildingAdminDashboard';
import SecurityGuardDashboard from './pages/SecurityGuardDashboard';
import FacilityBooking from './pages/FacilityBooking'; // <-- Add this import
import QRCodeGenerator from './components/QRCodeGenerator';

// Import mobile height positioning fixes
import './styles/mobile-height-fixes.css';
import BuildingExpenses from './pages/BuildingExpenses';
import NotificationsDashboard from './pages/NotificationsDashboard';
import MassNotifications from './pages/MassNotifications';
import PaymentCallback from './pages/PaymentCallback';
import { mobilePaymentService } from './services/mobilePayment';
import EmployeeDetails from './pages/EmployeeDetails';
import AssignedComplaints from './pages/AssignedComplaints';
import StaffAttendance from './pages/StaffAttendance';
import CommunityMarketplace from './pages/CommunityMarketplace';
import Announcements from './pages/Announcements';
import PayoutReportUpload from './pages/PayoutReportUpload';

// Route wrapper that sets readOnly based on user groups
const EmployeeDetailsRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner message="Checking permissions..." />;
  const groups = user?.groups || [];
  
  // Extract group names - handle both old format (strings) and new format (objects)
  const groupNames = groups.map(g => typeof g === 'string' ? g : g.name);
  
  // Check both groups and direct role property
  const editable = groupNames.some(g => ['admin', 'building_admin', 'maintenance_staff'].includes(g)) ||
                  ['super_admin', 'building_admin', 'maintenance_staff'].includes(user?.role);
  return <EmployeeDetails readOnly={!editable} />;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingSpinner message="Checking authentication..." />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Dashboard Route with group restriction
const DashboardRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return <LoadingSpinner message="Checking authentication..." />;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  const allowedGroups = ["admin", "maintenance_staff", "building_admin"];
  const userGroups = user.groups || [];
  
  // Extract group names - handle both old format (strings) and new format (objects)
  const groupNames = userGroups.map(g => typeof g === 'string' ? g : g.name);
  
  // Check both groups and direct role property
  const hasAccess = groupNames.some(g => allowedGroups.includes(g)) ||
                   allowedGroups.includes(user?.role);
  if (!hasAccess) {
    return <Box sx={{ p: 4 }}><h2>Not Authorized</h2><p>You do not have access to view the Dashboard.</p></Box>;
  }
  return children;
};

// Group-based Dashboard Route
const GroupDashboardRoute = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return <LoadingSpinner message="Checking authentication..." />;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  const groups = user.groups || [];
  
  // Extract group names - handle both old format (strings) and new format (objects)
  const groupNames = groups.map(g => typeof g === 'string' ? g : g.name);
  
  // Check both groups and direct role property
  if (groupNames.includes('admin') || user?.role === 'super_admin') {
    return <Dashboard />;
  }
  if (groupNames.includes('building_admin') || user?.role === 'building_admin') {
    return <BuildingAdminDashboard />;
  }
  if (groupNames.includes('maintenance_staff') || user?.role === 'maintenance_staff') {
    return <VisitorDashboard />; // Route maintenance staff to VisitorDashboard
  }
  if (groupNames.includes('owner') || groupNames.includes('tenant') || user?.role === 'resident') {
    return <OwnerTenantDashboard />;
  }
  return <Box sx={{ p: 4 }}><h2>Not Authorized</h2><p>You do not have access to view the Dashboard.</p></Box>;
};

// Building Admin Dashboard Route
const BuildingAdminDashboardRoute = () => {
  const { user, loading } = useAuth();
  if (loading) {
    return <LoadingSpinner message="Checking authentication..." />;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  // Extract group names - handle both old format (strings) and new format (objects)
  const userGroups = user?.groups || [];
  const groupNames = userGroups.map(g => typeof g === 'string' ? g : g.name);
  
  if (groupNames.includes('admin') || user?.role === 'super_admin') {
    return <Dashboard />;
  }
  if (groupNames.includes('building_admin') || user?.role === 'building_admin') {
    return <BuildingAdminDashboard />;
  }
  if (groupNames.includes('maintenance_staff') || user?.role === 'maintenance_staff') {
    return <VisitorDashboard />; // Route maintenance staff to VisitorDashboard
  }
  if (groupNames.includes('owner') || groupNames.includes('tenant') || ['owner', 'tenant'].includes(user?.role)) {
    return <OwnerTenantDashboard />;
  }
  return <Box sx={{ p: 4 }}><h2>Not Authorized</h2><p>You do not have access to view the Dashboard.</p></Box>;
};

const BuildingAdminStaffRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return <LoadingSpinner message="Checking authentication..." />;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  // Extract group names - handle both old format (strings) and new format (objects)
  const userGroups = user?.groups || [];
  const groupNames = userGroups.map(g => typeof g === 'string' ? g : g.name);
  const allowed = groupNames.some(g => ['building_admin', 'maintenance_staff', 'owner', 'tenant'].includes(g)) ||
                  ['building_admin', 'maintenance_staff', 'owner', 'tenant'].includes(user?.role);
  if (!allowed) {
    return <Box sx={{ p: 4 }}><h2>Not Authorized</h2><p>You do not have access to this page.</p></Box>;
  }
  return children;
};

const MaintenanceStaffOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner message="Checking permissions..." />;
  if (!user) return <Navigate to="/login" replace />;
  const groupNames = (user.groups || []).map(group => typeof group === 'string' ? group : group.name);
  const allowed = groupNames.includes('maintenance_staff') || user?.role === 'maintenance_staff';
  if (!allowed) {
    return <Box sx={{ p: 4 }}><h2>Not Authorized</h2><p>Only maintenance staff can access assigned complaints.</p></Box>;
  }
  return children;
};

const AttendanceRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner message="Checking attendance permissions..." />;
  if (!user) return <Navigate to="/login" replace />;
  const groupNames = (user.groups || []).map(group => typeof group === 'string' ? group.toLowerCase() : String(group?.name || '').toLowerCase());
  const allowed = groupNames.some(group => ['building_admin', 'maintenance_staff'].includes(group)) ||
    ['building_admin', 'maintenance_staff'].includes(user?.role);
  if (!allowed) return <Box sx={{ p: 4 }}><h2>Not Authorized</h2><p>Only building admins and maintenance staff can access attendance.</p></Box>;
  return children;
};

// Admin Only Route Component
const AdminStaffRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return <LoadingSpinner message="Checking authentication..." />;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  // Extract group names - handle both old format (strings) and new format (objects)
  const userGroups = user?.groups || [];
  const groupNames = userGroups.map(g => typeof g === 'string' ? g : g.name);
  const isAdminOrStaff = groupNames.some(g => ['admin'].includes(g)) || user?.role === 'super_admin';
  if (!isAdminOrStaff) {
    return <Box sx={{ p: 4 }}><h2>Not Authorized</h2><p>You do not have access to this page.</p></Box>;
  }
  return children;
};

// Building Admin Only Route Component (allows admin and building_admin)
const BuildingAdminOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return <LoadingSpinner message="Checking authentication..." />;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  // Extract group names - handle both old format (strings) and new format (objects)
  const userGroups = user?.groups || [];
  const groupNames = userGroups.map(g => typeof g === 'string' ? g : g.name);
  const allowed = groupNames.some(g => ['building_admin'].includes(g)) || user?.role === 'building_admin';
  if (!allowed) {
    return <Box sx={{ p: 4 }}><h2>Not Authorized</h2><p>You do not have access to this page.</p></Box>;
  }
  return children;
};

function App() {
  // Initialize mobile payment service if running on mobile platform
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      console.log('Initializing mobile payment service...');
      // The service is already initialized when imported
      console.log('Mobile platform detected:', Capacitor.getPlatform());
    }
  }, []);

  return (
    <AuthProvider>
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/profile" element={<Profile />} />
          
          {/* Payment callback should be accessible without Layout for mobile success flow */}
          <Route path="/payment-callback" element={
            <ProtectedRoute>
              <PaymentCallback />
            </ProtectedRoute>
          } />
          
          <Route path="/" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route index element={<GroupDashboardRoute />} />

            <Route path="visitors" element={<BuildingAdminStaffRoute><VisitorDashboard /></BuildingAdminStaffRoute>} />
            <Route path="vehicle-security" element={<BuildingAdminStaffRoute><SecurityGuardDashboard /></BuildingAdminStaffRoute>} />
            <Route path="assigned-complaints" element={<MaintenanceStaffOnlyRoute><AssignedComplaints /></MaintenanceStaffOnlyRoute>} />
            <Route path="staff-attendance" element={<AttendanceRoute><StaffAttendance /></AttendanceRoute>} />
            <Route path="community-marketplace" element={<CommunityMarketplace />} />
            <Route path="announcements" element={<ProtectedRoute><Announcements /></ProtectedRoute>} />

            <Route path="users" element={<AdminStaffRoute><Users /></AdminStaffRoute>} />
            <Route path="buildings" element={<AdminStaffRoute><Buildings /></AdminStaffRoute>} />
            <Route path="flats" element={<AdminStaffRoute><Flats /></AdminStaffRoute>} />
            <Route path="payments" element={<AdminStaffRoute><Payments /></AdminStaffRoute>} />
            <Route path="complaints" element={<AdminStaffRoute><Complaints /></AdminStaffRoute>} />
            <Route path="maintenance-staff" element={<AdminStaffRoute><MaintenanceStaff /></AdminStaffRoute>} />
            <Route path="VisitorDashboard" element={<AdminStaffRoute><VisitorDashboard /></AdminStaffRoute>} />
            <Route path="notifications" element={<AdminStaffRoute><Notifications /></AdminStaffRoute>} />
            <Route path="payout-report-upload" element={<AdminStaffRoute><PayoutReportUpload /></AdminStaffRoute>} />
            <Route path="notifications-dashboard" element={<ProtectedRoute><NotificationsDashboard /></ProtectedRoute>} />
            <Route path="facility-booking" element={
              <BuildingAdminStaffRoute>
                <FacilityBooking />
              </BuildingAdminStaffRoute>
            } />
            <Route path="expenses" element={
              <BuildingAdminStaffRoute>
                <BuildingExpenses />
              </BuildingAdminStaffRoute>
            } />
            <Route path="mass-notifications" element={
              <BuildingAdminStaffRoute>
                <MassNotifications />
              </BuildingAdminStaffRoute>
            } />
            <Route path="employee-details" element={
              <BuildingAdminStaffRoute>
                <EmployeeDetailsRoute />
              </BuildingAdminStaffRoute>
            } />
            <Route path="qr-generator" element={
              <BuildingAdminStaffRoute>
                <QRCodeGenerator />
              </BuildingAdminStaffRoute>
            } />
          </Route>
          {/* Redirect any unknown routes to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Box>
    </AuthProvider>
  );
}

export default App;
